// utils/scalability-utils.ts
import { RegionMatrices } from './region-utils';

/**
 * Returns the three scalability matrices
 * (names follow the functions from the original Python code)
 */
export function buildScalabilityMatrices(r: RegionMatrices) {
  /* Efficiency matrix E: rows = workloads  |  cols = parallel configurations */
  const E_original = r.efficiencies;               // shape [W][T]

  // Adjust efficiencies for the first workload
  const E = E_original.map((row, rowIndex) => {
    if (rowIndex === 0) {
      return row.map(efficiency => (efficiency > 1 ? 1 : efficiency));
    }
    return row;
  });

  /* -------- Strong scalability --------------------------------
     (Eᵗ - first column of Eᵗ)  then transpose back        */
  const strong: number[][] = transpose(
    transpose(E).slice(1)                 // discards the sequential baseline column
      .map(col => col.map((v, i) => v - transpose(E)[0][i]))
  );

  /* -------- “Scalability” normal (variation between workloads) ---- */
  const scalab: number[][] =
    E.slice(1).map((row, i) =>
      row.map((v, j) => {
        return v - E[0][j];
      })
    );

  /* -------- Weak scalability ----------------------------------- */
  const weak: number[][] = [];
  for (let i = 1; i < E.length; i++) {
    const row: number[] = [];
    for (let j = 1; j < E[0].length; j++) {
      const miv = Math.min(i, j);
      row.push(E[i][j] - E[i - miv][j - miv]);
    }
    weak.push(row);
  }

  return { strong, scalab, weak };
}

/**
 * Returns the three RELATIVE scalability matrices
 * (comparing with the previous neighbor instead of the first)
 */
export function buildRelativeScalabilityMatrices(r: RegionMatrices) {
  /* Efficiency matrix E: rows = workloads  |  cols = parallel configurations */
  const E_original = r.efficiencies; // shape [W][T]

  // Adjust efficiencies for the first workload (same as in buildScalabilityMatrices)
  const E = E_original.map((row, rowIndex) => {
    if (rowIndex === 0) {
      return row.map(efficiency => (efficiency > 1 ? 1 : efficiency));
    }
    return row;
  });

  /* -------- Relative Strong scalability --------------------------------
     Compares E[w][t] with E[w][t-1]
     (Et[t][w] - Et[t-1][w]) then transpose back
  */
  let relativeStrong: number[][] = [];
  if (E.length > 0 && E[0] && E[0].length > 0) {
    const Et = transpose(E); // Et is T x W
    if (Et.length > 1) { // Need at least 2 parallel configurations to compare
      const relativeStrong_transposed = Et.slice(1).map((currentRow, rowIndex) => {
        const prevRow = Et[rowIndex]; // This is Et[rowIndex]
        return currentRow.map((val, colIndex) => val - prevRow[colIndex]);
      });
      relativeStrong = transpose(relativeStrong_transposed); // W x (T-1)
    } else {
      // If T <= 1, relativeStrong is W x 0, represented as an array of W empty arrays
      // or simply [] if W is also 0. transpose([]) handles this.
      // To be consistent, if T <=1, result should be W x 0.
      // If E is Wx1, Et is 1xW. Et.slice(1) is []. transpose([]) is [].
      // If E is Wx0, Et is 0xW. Et.slice(1) is []. transpose([]) is [].
      // This is fine.
    }
  }


  /* -------- Relative “Scalability” normal (variation between workloads) ----
     Compares E[w][t] with E[w-1][t]
  */
  let relativeScalab: number[][] = [];
  if (E.length > 1 && E[0] && E[0].length > 0) { // Need at least 2 workloads to compare
    relativeScalab = E.slice(1).map((currentRow, rowIndex) => {
      const prevRow = E[rowIndex]; // This is E[rowIndex]
      return currentRow.map((val, colIndex) => val - prevRow[colIndex]);
    }); // (W-1) x T
  }

  /* -------- Relative Weak scalability -----------------------------------
     Compares E[w][t] with E[w-1][t-1]
  */
  const relativeWeak: number[][] = [];
  if (E.length > 1 && E[0] && E[0].length > 1) { // Need at least 2 workloads and 2 parallel configurations
    for (let i = 1; i < E.length; i++) {
      const row: number[] = [];
      for (let j = 1; j < E[0].length; j++) {
        row.push(E[i][j] - E[i-1][j-1]);
      }
      relativeWeak.push(row); // (W-1) x (T-1)
    }
  }

  return { relativeStrong, relativeScalab, relativeWeak };
}

/* generic util */
function transpose<T>(m: T[][]): T[][] {
  if (!m || m.length === 0 || !m[0]) { // Handles m = [] or m = [[]] or m = [[], []]
    return [];
  }
  // If m[0].length is 0 (e.g. m = [[], [], []] which is a 3x0 matrix),
  // it should return a 0x3 matrix, which is [].
  if (m[0].length === 0) {
    return [];
  }
  return m[0].map((_, colIndex) => m.map(row => row[colIndex]));
}
